import { DataGrid } from "@mui/x-data-grid";

const UncheckedIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="0.5" y="0.5" width="13" height="13" rx="1.5" fill="white" stroke="#DAE4D5"/>
  </svg>
)

// Comment Set #1, Pipeline 4: "Update checkbox icon on the header row. It's currently an unbranded
// blue design."
//
// Only the header can be indeterminate, and that is the whole of the defect. GridHeaderCheckbox
// (@mui/x-data-grid 8.29.2) renders `<slots.baseCheckbox indeterminate={isIndeterminate} ...
// {...slotProps.baseCheckbox}>`, so the `icon` and `checkedIcon` below already reached it — but MUI
// draws `indeterminateIcon` in that third state and nothing was overriding it, so ticking some rows
// but not all put MUI's own filled blue square on the header. Unchecked and fully checked were ours
// the whole time, which is why it reads as intermittent.
//
// Measured out of the export rather than derived from CheckedIcon: `sheet-heading/Frame 176/
// codex:check` is 13x13 r 1.5 filled #E7F0E2 on a #4CBC18 edge, and the dash inside it is a layer
// named `check-minus`, `M1736 1268H1742`, round cap — 3.5 to 9.5 at 6.5 measured from the rect's
// own corner (1732.5, 1261.5), which is 4 to 10 at 7 in the viewBox below, where that corner sits
// on 0.5. Two origins, one dash; the code is in viewBox coordinates.
const IndeterminateIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="0.5" y="0.5" width="13" height="13" rx="1.5" fill="#E7F0E2" stroke="#4CBC18"/>
    <path d="M4 7H10" stroke="#4CBC18" strokeLinecap="round"/>
  </svg>
)

const CheckedIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="0.5" y="0.5" width="13" height="13" rx="1.5" fill="#F3FFEB" stroke="#4CBC18"/>
    <path d="M3.5 7L5.5 9.5L10.5 4.5" stroke="#4CBC18" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
)

const PipelineGrid = ({
  rows,
  columns,
  selectedRows,
  onRowSelectionChange,
}) => {
  return (
    <div className="w-full overflow-hidden rounded-[24px] bg-white border border-[#DCEBD4]">
      <DataGrid
        rows={rows}
        columns={columns}
        rowSelectionModel={{ type: 'include', ids: new Set(selectedRows) }}
        onRowSelectionModelChange={(model) => {
            if (model?.type === 'include') {
                onRowSelectionChange([...model.ids]);
            } else {
                onRowSelectionChange([]);
            }
        }}
        checkboxSelection
        disableColumnMenu
        disableRowSelectionOnClick
        hideFooter
        autoHeight
        rowHeight={76}
        slotProps={{
          baseCheckbox: {
            icon: <UncheckedIcon />,
            checkedIcon: <CheckedIcon />,
            indeterminateIcon: <IndeterminateIcon />,
          },
        }}
        sx={{
          border: "none",
          fontFamily: 'Figtree',
          '--DataGrid-rowBorderColor': '#DAE4D5',
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: "#DCEBD4",
            borderBottom: "none",
          },

          "& .MuiDataGrid-columnHeaderTitle": {
            fontSize: 14,
            fontWeight: 700,
            color: "#17270D",
          },

          "& .MuiDataGrid-cell": {
            display: "flex",
            alignItems: "center",
            outline: "none !important",
          },

          "& .MuiDataGrid-row": {
            backgroundColor: "#FFFFFF",
          },

          // Comment Set #1, ARV 7.4 — "apply to Pipeline table as well". This grid had no hover rule
          // at all, so it was falling through to MUI's own action.hover, a grey wash from the
          // default theme. The two selected rules below stay #E8F5E1 and stay above this one in
          // specificity (.MuiDataGrid-row.Mui-selected:hover beats .MuiDataGrid-row:hover), which is
          // what keeps a ticked row reading as ticked while the pointer is over it.
          "& .MuiDataGrid-row:hover": {
            backgroundColor: "#F7F9F6",
          },

          "& .MuiDataGrid-row.Mui-selected": {
            backgroundColor: "#E8F5E1",
          },

          "& .MuiDataGrid-row.Mui-selected:hover": {
            backgroundColor: "#E8F5E1",
          },

          // Comment Set #2, 1.1.1 (letter 104): the box's edge goes #4CBC18 under the pointer, rows and
          // header alike. Checked and indeterminate are already #4CBC18; CSS beats the rect's stroke attribute.
          "& .MuiCheckbox-root": {
            padding: "5px",
            "&:hover": { backgroundColor: "transparent" },
            "&:hover rect": { stroke: "#4CBC18" },
          },

          "& .MuiDataGrid-columnSeparator": {
            display: "none",
          },

          "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-cell:focus": {
            outline: "none",
          },
          '& .MuiDataGrid-columnHeader, & .MuiDataGrid-cell': {
            padding: '0 !important',
          },

          // Added's value carries pl-[12px] (columns.js) so a truncated Tags pill doesn't abut the
          // date digits; the header text had no matching inset, so it sat 12px left of where the
          // value starts (qa-verifier finding, 25.08.2026). The grid-wide rule above stops at
          // .MuiDataGrid-columnHeader itself, one level above .MuiDataGrid-columnHeaderTitleContainer
          // (the flex box holding the title and, once sortable, the sort-icon container), so this
          // selector reaches past it rather than fighting its !important. headerClassName scopes it to
          // this one column — every other header stays at its own zero padding.
          '& .pipeline-added-header .MuiDataGrid-columnHeaderTitleContainer': {
            paddingLeft: '12px',
          },
          // Pinned by PipelineGrid.test.jsx — headerClassName alone is not the fix, and deleting this
          // rule on its own left the whole suite green until that file existed.
        }}
      />
    </div>
  );
};

export default PipelineGrid;
